import { createSlice } from "@reduxjs/toolkit";
import { plainAxios, axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";
import {
  clearUserFromStorage,
  getAuthStatusFromStorage,
  getUseFromStorage,
  setUserToStorage,
  setServiceProviderEnabled,
} from "../../../../utils/storage";
import {
  storeTokens,
  clearTokens,
  isTWA,
} from "../../../../utils/authHelper";
import { listMyWorkspaces } from "../../Common/Workspace/WorkspaceSlice";

import logger from "../../../../utils/logger";
import oneSignalService from "../../../../services/oneSignalService";


// ── Initial state ─────────────────────────────────────────────────────────────
const initialState = {
  errors: null,
  isLoading: false,
  isSuccess: false,
  isAuthenticated: getAuthStatusFromStorage(),
  userData: getUseFromStorage(),
  accessToken: null,
  refreshToken: null,
  isNewUser: false,
  newUserData: null,
  isInitializing: !getAuthStatusFromStorage(), // ✅ Zero-latency: Don't block UI if locally authenticated
};

// ── Slice ─────────────────────────────────────────────────────────────────────
const LoginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setErrors: (state, action) => {
      // Ensure errors is always a string or null to prevent React rendering crashes
      state.errors = typeof action.payload === 'string' ? action.payload : (action.payload?.message || null);
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setSuccess: (state, action) => {
      state.isSuccess = action.payload;
    },
    setIsAuthenticated: (state, action) => {
      const value = Boolean(action.payload);
      state.isAuthenticated = value;
      localStorage.setItem("isAuthenticated", value.toString());
    },
    setUserData: (state, action) => {
      state.userData = action.payload;
      setUserToStorage(action.payload);
    },
    clearAuth: (state) => {
      state.userData = null;
      state.isAuthenticated = false;
      state.errors = null;
      state.isSuccess = false;
      state.accessToken = null;
      state.refreshToken = null;
      clearUserFromStorage();
      clearTokens(); // ✅ Clears memory + bkz_rt from localStorage/sessionStorage
    },

    setIsNewUser: (state, action) => {
      state.isNewUser = action.payload;
    },
    setNewUserData: (state, action) => {
      state.newUserData = action.payload;
    },
    clearNewUserData: (state) => {
      state.isNewUser = false;
      state.newUserData = null;
    },
    setInitializing: (state, action) => {
      state.isInitializing = action.payload;
    },
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// Register OTP Request
// ─────────────────────────────────────────────────────────────────────────────
export const register_OTP_request = (email) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setSuccess(false));

  try {
    const response = await plainAxios.post("users/auth/email/request", { email });

    if (response.data?.status === "success") {
      showNotification({
        message: response.data.message,
        theme: "light",
        type: "success",
      });
    }
  } catch (error) {
    const errMsg = error?.response?.data?.message || "Something went wrong. Please try again.";
    dispatch(setErrors(errMsg));
    showNotification({
      message: errMsg,
      theme: "light",
      type: "error",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Register OTP Verify
// ─────────────────────────────────────────────────────────────────────────────
export const register_OTP_verify = (email, otp) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setSuccess(false));

  try {
    const response = await plainAxios.post("users/auth/email/verify", { email, otp });

    if (response.data?.status === "success") {
      dispatch(setSuccess(true));
      showNotification({
        message: response.data.message,
        theme: "light",
        type: "success",
      });
    }
  } catch (error) {
    const errMsg = error?.response?.data?.message || "Verification failed. Please try again.";
    dispatch(setErrors(errMsg));
    showNotification({ message: errMsg, theme: "light", type: "error" });
  } finally {
    dispatch(setLoading(false));
  }
};

// ── Helper: Handle Auth Response ─────────────────────────────────────────────
const handleAuthResponse = (data, dispatch, logPrefix = "Auth") => {
  const { user, tokens, isNewUser, isServiceProviderEnabled, accessToken: bodyAccessToken } = data;

  // 1. Token storage (Flexible extraction for body mode vs cookie mode)
  const tokensData = tokens || data; // Backend might return {tokens: {at, rt}} or {at, rt} directly
  const at = tokensData?.accessToken || bodyAccessToken;
  const rt = tokensData?.refreshToken || tokensData?.bkz_rt; // Fallback to bkz_rt key

  if (at && rt) {
    storeTokens(at, rt);
    logger.log(`✅ ${logPrefix} tokens stored (Body Mode)`, { mode: isTWA() ? 'TWA' : 'localhost/testing', hasRT: true });
  } else if (at) {
    storeTokens(at, null);
    logger.log(`✅ ${logPrefix} access token cached (Cookie Mode)`);
  }

  // 2. New User flow
  if (isNewUser) {
    dispatch(setNewUserData({ user, tokens }));
    dispatch(setIsNewUser(true));
    return { success: true, isNewUser: true };
  }

  // 3. Authenticated state
  dispatch(setUserData(user));
  dispatch(setIsAuthenticated(true));
  dispatch(setSuccess(true));

  // 4. OneSignal Identity
  oneSignalService.login(user.userId);

  // 5. Service Provider flag
  setServiceProviderEnabled(isServiceProviderEnabled ?? false);

  // 6. Eagerly seed workspace list to ensure Hub visibility
  dispatch(listMyWorkspaces(true));

  return { success: true, isNewUser: false };
};

// ─────────────────────────────────────────────────────────────────────────────
// Login with Google
// ─────────────────────────────────────────────────────────────────────────────
export const loginWithGoogle = (credentialResponse, rememberMe = true) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await plainAxios.post("users/auth/google", {
      credential: credentialResponse.credential,
      rememberMe,
    });

    if (response.data?.status === "success") {
      const result = handleAuthResponse(response.data.data, dispatch, "Google");

      showNotification({
        message: result.isNewUser ? "Welcome! Please complete your profile." : "Google login successful!",
        theme: "light",
        type: "success",
      });

      return result;
    }
  } catch (error) {
    const errMsg = error?.response?.data?.message || "Google login failed. Please try again.";
    dispatch(setErrors(errMsg));
    showNotification({ message: errMsg, theme: "light", type: "error" });
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Request WhatsApp OTP
// ─────────────────────────────────────────────────────────────────────────────
export const requestWhatsAppLogin = (phone) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await plainAxios.post("users/auth/whatsapp/otp/request", { phone });

    

    if (response.data?.status === "success") {
      dispatch(setSuccess(true));
      showNotification({
        message: "OTP sent to your WhatsApp! Check your messages.",
        theme: "light",
        type: "success",
      });
      return { success: true, isRegistered: response.data.data?.isRegistered };
    } else {
      dispatch(setSuccess(false));
      showNotification({
        message: response.data?.message || "Failed to send OTP",
        theme: "light",
        type: "error",
      });
      return { success: false };
    }
  } catch (error) {
    
    const errMsg = error?.response?.data?.message || "Failed to send OTP. Please try again.";
    dispatch(setErrors(errMsg));
    dispatch(setSuccess(false));
    showNotification({ message: errMsg, theme: "light", type: "error" });
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Verify WhatsApp OTP
// ─────────────────────────────────────────────────────────────────────────────
export const verifyWhatsAppOTP = (phone, otp, rememberMe = false, isVerificationOnly = false) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const url = isVerificationOnly ? "users/auth/whatsapp/otp/verify-only" : "users/auth/whatsapp/otp/verify";
    const response = await plainAxios.post(url, {
      phone,
      otp,
      rememberMe,
    });

    if (response.data?.status === "success") {
      if (isVerificationOnly) {
        showNotification({ message: "Number verified successfully!", type: "success" });
        return { success: true };
      }

      const result = handleAuthResponse(response.data.data, dispatch, "WhatsApp");

      showNotification({
        message: result.isNewUser ? "Welcome! Please complete your profile." : "Login successful!",
        theme: "light",
        type: "success",
      });

      return result;
    }
    return { success: false };
  } catch (error) {
    const errMsg = error?.response?.data?.message || "Invalid OTP. Please try again.";
    dispatch(setErrors(errMsg));
    showNotification({ message: errMsg, theme: "light", type: "error" });
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Complete New User Registration
// ─────────────────────────────────────────────────────────────────────────────
export const completeNewUserRegistration = (firstName, lastName, location, dob, gender) => async (dispatch, getState) => {
  dispatch(setLoading(true));
  try {
    const { newUserData } = getState().login;

    if (!newUserData) {
      throw new Error("No new user data found");
    }

    const profileData = {
      firstName: firstName?.trim(),
      lastName: lastName?.trim(),
    };

    if (location) {
      profileData.location = typeof location === 'string' ? { address: location.trim() } : location;
    }
    if (dob) {
      profileData.dob = dob;
    }
    if (gender) {
      profileData.gender = gender;
    }

    const response = await axiosInstance.patch("users/profile", profileData);

    if (response.data?.status === "success") {
      const updatedProfile = response.data.data;

      const userData = {
        ...newUserData.user,
        firstName: firstName?.trim(),
        lastName: lastName?.trim(),
        ...(updatedProfile && {
          email: updatedProfile.email || newUserData.user.email,
          phoneNumber: updatedProfile.phoneNumber || newUserData.user.phoneNumber,
          profilePicture: updatedProfile.profilePicture || newUserData.user.profilePicture,
          dob: updatedProfile.dob || dob,
          gender: updatedProfile.gender || gender,
          addresses: updatedProfile.addresses || (location ? { home: location.trim() } : undefined),
        }),
      };

      dispatch(setUserData(userData));
      dispatch(setIsAuthenticated(true));
      dispatch(setIsNewUser(false));
      dispatch(setNewUserData(null));
      dispatch(setSuccess(true));

      // ✅ OneSignal: Identifty link
      oneSignalService.login(userData.userId);

      showNotification({
        message: "Welcome! Your account has been created.",
        theme: "light",
        type: "success",
      });
    }
  } catch (error) {
    const errMsg = error?.response?.data?.message || "Failed to update profile. Please try again.";
    dispatch(setErrors(errMsg));
    showNotification({ message: errMsg, theme: "light", type: "error" });
  } finally {
    dispatch(setLoading(false));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Register
// ─────────────────────────────────────────────────────────────────────────────
export const register = (email, firstName, lastName, phone, location, navigate, rememberMe = false) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setSuccess(false));

  try {
    const response = await plainAxios.post("users/auth/register", {
      email, firstName, lastName, phone, location, rememberMe,
    });

    if (response.data?.status === "success") {
      const result = handleAuthResponse(response.data.data, dispatch, "Register");

      showNotification({
        message: response.data.message || "Registration successful!",
        theme: "light",
        type: "success",
      });

      if (navigate) navigate("/");
      return result;

    } else {
      dispatch(setIsAuthenticated(false));
      showNotification({
        message: response.data?.message || "Registration Failed",
        theme: "light",
        type: "error",
      });
      return { success: false };
    }
  } catch (error) {
    const errMsg = error?.response?.data?.message || "Registration failed. Please try again.";
    dispatch(setErrors(errMsg));
    showNotification({ message: errMsg, theme: "light", type: "error" });
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Logout
// ─────────────────────────────────────────────────────────────────────────────
export const logOut = (navigate) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setErrors(null));

  const performLocalLogout = () => {
    dispatch(clearAuth());          // calls clearTokens() internally
    sessionStorage.clear();
    localStorage.removeItem("isAuthenticated");
    clearTokens();
    clearUserFromStorage();
    localStorage.removeItem("serviceProviderEnabled"); // ✅ Clear SP flag on logout
    // ❌ Do NOT remove twa_mode. The user is still using the TWA app even if logged out.
    
    // ✅ OneSignal: Logout
    oneSignalService.logout();
  };

  try {
    // ✅ Fix 2: always withCredentials: true
    // Cookie mode: backend clears httpOnly cookie
    // Token mode: backend ignores cookies — no harm done
    await plainAxios.post("users/auth/logout", {}, { withCredentials: true });

    performLocalLogout();

    showNotification({
      message: "Logout Successful",
      theme: "light",
      type: "success",
    });

    if (navigate) navigate("/login");

  } catch {
    // Force logout even if API fails
    performLocalLogout();

    showNotification({
      message: "Logged out locally",
      theme: "light",
      type: "info",
    });

    if (navigate) navigate("/login");
  } finally {
    dispatch(setLoading(false));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Exports
// ─────────────────────────────────────────────────────────────────────────────
export const {
  setErrors,
  setLoading,
  setIsAuthenticated,
  setIsNewUser,
  setNewUserData,
  clearNewUserData,
  setUserData,
  setSuccess,
  clearAuth,
  setInitializing,
} = LoginSlice.actions;

export default LoginSlice.reducer;
